// Core packages
import { Component, Inject, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';

// Third party packages
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { TranslateService } from '@ngx-translate/core';
import { MatProgressButtonOptions } from 'mat-progress-buttons';
import moment from 'moment';
import { CookieService } from 'ngx-cookie-service';
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import { Subscription } from "rxjs";

// Custom packages
import { PropertiesService } from "src/app/modules/properties/properties.service";
import { SettingsService } from "src/app/modules/settings/settings.service";
import { PropertyInterface } from "src/app/shared/models/property/property.interface";
import { Property } from "src/app/shared/models/property/property.model";
import { ConfigService } from 'src/app/shared/services/config.service';
import { HelperService } from "src/app/shared/services/helper.service";
import DocumentTypeStructure from "../../documentTypes";

@Component({
  selector: 'app-export-ras-docs-dialog',
  templateUrl: './export-ras-docs-dialog.component.html',
  styleUrls: ['./export-ras-docs-dialog.component.scss']
})
export class ExportRasDocsDialogComponent implements OnInit {
  lang = this.translateService.currentLang || this.translateService.defaultLang;

  private subscriptions: Subscription[] = [];

  // BEGIN - Add subUser form
  // BEGIN - ngx-formly
  form: FormGroup = new FormGroup({});
  model: any = {};
  fields: FormlyFieldConfig[] = [];
  options: FormlyFormOptions = {};
  // END - ngx-formly

  formBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant('common.cta.send'),
    spinnerSize: 19,
    raised: true,
    stroked: true,
    flat: false,
    fab: false,
    buttonColor: 'primary',
    spinnerColor: 'primary',
    fullWidth: false,
    disabled: false,
    mode: 'indeterminate',
    buttonIcon: {
      fontSet: 'fal',
      fontIcon: 'fa-paper-plane',
      inline: true,
    },
  };
  // END - Add communication form

  dialogIsLoading = true;

  public property: Property = new Property();
  propertyData: PropertyInterface;
  propertyName = "";
  propertyAddress = "";
  subscriptionType = "FULL";
  documentArchiveId: string;
  public documentTypeStructure: DocumentTypeStructure =
    new DocumentTypeStructure();
  documentTemplateId = "";
  documentTemplate: any[] = [];
  documentArchive: any[] = [];

  rasDocs: any[] = [];

  totalPages = 2;
  pdfDocPerPage = 4;
  supplierLong = 50;// long supplier interruption

  /**
   * Class constructor
   */
  constructor(
    public dialogRef: MatDialogRef<ExportRasDocsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public configService: ConfigService,

    private cookieService: CookieService,
    private translateService: TranslateService,
    private propertiesService: PropertiesService,
    private helperService: HelperService,
    private settingsService: SettingsService,
  ) { }

  /**
   * Init component
   *
   */
  ngOnInit(): void {
    // Get current lang
    this.lang = this.cookieService.get('lang') || this.configService.defaultLang;

    const host = `${this.configService.frontEndHost}`;

    this.subscriptions.push(
      this.propertiesService.get(this.data.propertyId).subscribe(
        (res) => {
          if (res.status) {
            this.documentArchiveId = res.data.documentArchive;
            this.property.setData(res.data);
            this.propertyData = res.data;
            this.propertyName = res.data.name;
            this.propertyAddress = `${res.data.address.route} ${res.data.address.streetNumber} - ${res.data.address.postalCode} ${res.data.address.city}`;

            this.subscriptions.push(
              this.settingsService.getDocument().subscribe(
                (res) => {
                  if (res.status) {
                    if (
                      res.data &&
                      Array.isArray(res.data) &&
                      res.data.length > 0 &&
                      res.data[0].area &&
                      Array.isArray(res.data[0].area)
                    ) {
                      this.documentTemplateId = res.data[0]._id;
                      this.documentTemplate = res.data[0].area;
                      this.subscriptions.push(
                        this.propertiesService.getRasDocs(this.data.propertyId).subscribe(
                          (res2) => {
                            if (
                              res2.data.status &&
                              res2.data.rasDocs &&
                              Array.isArray(res2.data.rasDocs)
                            ) {
                              let index = 0;
                              let curDocSection = '';
                              let countDocsInSection = 0;
                              for (const rasDoc of res2.data.rasDocs) {
                                const type = rasDoc.docTitle;
                                const docSection = rasDoc.titleLevel1;
                                const documentKey = rasDoc.key;

                                if (curDocSection !== docSection) {
                                  curDocSection = docSection;
                                  this.totalPages += Math.floor(countDocsInSection / this.pdfDocPerPage) + ((countDocsInSection % this.pdfDocPerPage) > 0 ? 1 : 0);
                                  
                                  countDocsInSection = 0;
                                }
          
                                const section = this.documentTypeStructure.getDocRoute2(documentKey, this.documentTemplate);
          
                                if (
                                  rasDoc.data &&
                                  Array.isArray(rasDoc.data) &&
                                  rasDoc.data.length
                                ) {
                                  for (const element of rasDoc.data) {
                                    index += 1;
                                    let documentId = 'generic';
                                    if (
                                      element.structureObjectId !== '' &&
                                      element.structureObjectId !== '-1'
                                    ) {
                                      documentId = element.docId;
                                    }
                                    const subDocumentId = element.subDocId
          
                                    const qrcode =
                                      `${host}/qrcode/${this.documentArchiveId}/${section}/${documentId}/${documentKey}/${subDocumentId}`;
          
                                    let reevaluated = false;
                                    if (
                                      rasDoc.reevaluated &&
                                      moment(element.data.dataDocumento).isValid() &&
                                      moment(element.data.dataDocumento).diff(moment('2000-01-01'), 'days') < 0
                                    ) {
                                      reevaluated = true;
                                    }
                                    countDocsInSection += 1;
                                    this.documentArchive.push({
                                      index,
                                      docSection,
                                      type,
                                      structureObjectName: rasDoc.titleLevel3,
                                      ragioneSocialeFornitore: element.data.ragioneSocialeFornitore ? element.data.ragioneSocialeFornitore : '',
                                      dataDocumento: element.data.dataDocumento,
                                      dateExpired: element.dateExpired,
                                      qrcode,
                                      reevaluated,
                                    });
                                  }
                                } else {
                                  index += 1;
                                  countDocsInSection += 1;
                                  this.documentArchive.push({
                                    index,
                                    docSection,
                                    type,
                                    structureObjectName: rasDoc.titleLevel3,
                                    ragioneSocialeFornitore: '',
                                    dataDocumento: '',
                                    dateExpired: '',
                                    qrcode: '',
                                    urgent: rasDoc.urgent,
                                  });
                                }
                              }
                              this.totalPages += Math.floor(countDocsInSection / 7) + ((countDocsInSection % 7) > 0 ? 1 : 0);
                            }
          
                            this.dialogIsLoading = false;
                          },
                          (err) => {
                            this.dialogIsLoading = false;
                            this.helperService.handleError(err);
                          }
                        )
                      );
                    }
                  }
                }
              )
            );

            
          }
        },
        (err) => {
          this.dialogIsLoading = false;
          this.helperService.handleError(err);
        }
      )
    );
  }

  /**
   * RAS docs export in PDF fromat
   *
   * @since 1.0.0
   */
  onExportFile(): void {
    const createdAt = moment().format('DD-MM-YYYY HH:mm:ss')
    const pdfContent: any[] = [];
    const paragraphMargin = [0, 10, 0, 10]; // [left, top, right, bottom]
    const paragraphMarginTop = [0, 40, 0, 10];

    // Page 1
    pdfContent.push({
      text: [
        {
          text: this.translateService.instant("properties.ras.title.text"),
          fontSize: 20,
          // bold: this.translateService.instant("properties.ras.title.bold"),
          alignment: 'center',
        },
      ],
      margin: [0, 60, 0, 10],
    });
    pdfContent.push({
      text: [
        {
          text: this.data.companyName,
          fontSize: 20,
          // bold: this.translateService.instant("properties.ras.title.bold"),
          alignment: 'center',
        },
      ],
      margin: [0, 30, 0, 10],
    });
    pdfContent.push({
      text: [
        {
          text: this.translateService.instant("properties.ras.subTitle.text"),
          fontSize: 18,
          // bold: this.translateService.instant("properties.ras.subTitle.bold"),
          bold: true,
          alignment: 'center',
        },
      ],
      margin: [0, 40, 0, 10],
    });
    pdfContent.push({
      text: [
        {
          text: this.propertyName,
          fontSize: 16,
          bold: false,
          alignment: 'center',
        }
      ],
      margin: [0, 80, 0, 10],
    });
    pdfContent.push({
      text: [
        {
          text: this.propertyAddress,
          fontSize: 16,
          bold: false,
          alignment: 'center',
        }
      ],
      margin: [0, 10, 0, 10],
    });
    pdfContent.push({
      text: [
        {
          text: this.translateService.instant("properties.ras.creationDate.text"),
          fontSize: 16,
          bold: false,
          alignment: 'center',
        },
        {
          text: createdAt,
          fontSize: 16,
          bold: false,
          alignment: 'center',
        }
      ],
      margin: [0, 80, 0, 10],
    });
    pdfContent.push({
      text: [
        {
          text: this.translateService.instant("properties.ras.numberPage.text"),
          fontSize: 16,
          bold: false,
          alignment: 'center',
        },
        {
          text: `${this.totalPages} `,
          fontSize: 16,
          bold: false,
          alignment: 'center',
        },
        {
          text: this.translateService.instant("properties.ras.page.text"),
          fontSize: 16,
          bold: false,
          alignment: 'center',
        },
      ],
      margin: [0, 10, 0, 10],
    });
    pdfContent.push({
      text: [
        {
          text: this.translateService.instant("properties.ras.notePage1.text"),
          fontSize: 18,
          bold: false,
          alignment: 'center',
        }
      ],
      margin: [0, 80, 0, 10],
      pageBreak: 'after'
    });
    // Page 2
    pdfContent.push({
      text: this.translateService.instant("properties.ras.p1.text"),
      fontSize: 12,
      bold: this.translateService.instant("properties.ras.p1.bold"),
      margin: paragraphMargin,
      alignment: 'justify',
    });
    pdfContent.push({
      text: this.translateService.instant("properties.ras.p2.text"),
      fontSize: 12,
      bold: this.translateService.instant("properties.ras.p2.bold"),
      margin: paragraphMargin,
      alignment: 'justify',
    });
    pdfContent.push({
      text: [
        {
          text: this.translateService.instant("properties.ras.p3.subText1.text"),
          fontSize: 12,
          bold: this.translateService.instant("properties.ras.p3.subText1.bold"),
          italics: true,
        },
        {
          text: this.translateService.instant("properties.ras.p3.subText2.text"),
          fontSize: 12,
          bold: this.translateService.instant("properties.ras.p3.subText2.bold"),
          italics: true,
        },
      ],
      margin: paragraphMargin,
      alignment: 'justify',
    });
    pdfContent.push({
      text: this.translateService.instant("properties.ras.p4.text"),
      fontSize: 12,
      bold: this.translateService.instant("properties.ras.p4.bold"),
      margin: paragraphMargin,
      alignment: 'justify',
    });
    pdfContent.push({
      text: [
        {
          text: this.translateService.instant("properties.ras.p5.subText1.text"),
          fontSize: 12,
          bold: this.translateService.instant("properties.ras.p5.subText1.bold"),
        },
        {
          text: this.translateService.instant("properties.ras.p5.subText2.text"),
          fontSize: 12,
          bold: this.translateService.instant("properties.ras.p5.subText2.bold"),
        },
        {
          text: this.translateService.instant("properties.ras.p5.subText3.text"),
          fontSize: 12,
          bold: this.translateService.instant("properties.ras.p5.subText3.bold"),
        },
      ],
      margin: paragraphMargin,
      alignment: 'justify',
    });
    pdfContent.push({
      text: this.translateService.instant("properties.ras.p6.text"),
      fontSize: 12,
      bold: this.translateService.instant("properties.ras.p6.bold"),
      margin: paragraphMargin,
      alignment: 'justify',
    });
    pdfContent.push({
      text: this.translateService.instant("properties.ras.p7.text"),
      fontSize: 12,
      bold: this.translateService.instant("properties.ras.p7.bold"),
      margin: paragraphMargin,
      alignment: 'justify',
    });
    pdfContent.push({
      text: this.translateService.instant("properties.ras.p8.text"),
      fontSize: 12,
      bold: this.translateService.instant("properties.ras.p8.bold"),
      margin: paragraphMargin,
      alignment: 'justify',
    });
    pdfContent.push({
      text: [
        {
          text: this.translateService.instant("properties.ras.p9.subText1.text"),
          fontSize: 12,
          bold: this.translateService.instant("properties.ras.p9.subText1.bold"),
        },
        {
          text: this.translateService.instant("properties.ras.p9.subText2.text"),
          fontSize: 12,
          bold: this.translateService.instant("properties.ras.p9.subText2.bold"),
          color: '#fc0303'
        },
        {
          text: this.translateService.instant("properties.ras.p9.subText3.text"),
          fontSize: 12,
          bold: this.translateService.instant("properties.ras.p9.subText3.bold"),
        },
      ],
      margin: paragraphMargin,
      alignment: 'justify',
    });
    pdfContent.push({
      text: this.translateService.instant("properties.ras.p10.text"),
      fontSize: 12,
      bold: this.translateService.instant("properties.ras.p10.bold"),
      margin: paragraphMargin,
      alignment: 'justify',
    });
    pdfContent.push({
      text: this.translateService.instant("properties.ras.p11.text"),
      fontSize: 12,
      bold: this.translateService.instant("properties.ras.p11.bold"),
      margin: paragraphMargin,
      alignment: 'justify',
      pageBreak: 'after',
      pageOrientation: 'landscape'
    });

    // Page 3
    let tableBody = [];
    
    let docArea = '';
    let countDocs = 0;
    for (const element of this.documentArchive) {
      // subdivision by macro area
      if (docArea !== element.docSection) {
        if (docArea !== '') {
          pdfContent.push({
            // layout: 'lightHorizontalLines', // optional
            layout: 'customLayout',
            table: {
              // headers are automatically repeated if the table spans over multiple pages
              // you can declare how many rows should be treated as headers
              headerRows: 1,
              widths: [200, 200, 100, 100, 'auto'],
              heights: 20,
              body: tableBody,
            },
            pageBreak: 'after',
            pageOrientation: 'landscape'
          });
        }

        countDocs = 0;
        tableBody = [];
        tableBody.push( [
          {
            text: this.translateService.instant("properties.ras.table.co1"),
            fontSize: 12,
            style: [
              {
                'color': '#000000'
              }
            ],
            bold: true,
            alignment: 'center',
          },
          {
            text: this.translateService.instant("properties.ras.table.co2"),
            fontSize: 12,
            style: [
              {
                'color': '#000000'
              }
            ],
            bold: true,
            alignment: 'center',
          },
          {
            text: this.translateService.instant("properties.ras.table.co3"),
            fontSize: 12,
            style: [
              {
                'color': '#000000'
              }
            ],
            bold: true,
            alignment: 'center',
          },
          {
            text: this.translateService.instant("properties.ras.table.co4"),
            fontSize: 12,
            style: [
              {
                'color': '#000000'
              }
            ],
            bold: true,
            alignment: 'center',
          },
          {
            text: this.translateService.instant("properties.ras.table.co5"),
            fontSize: 12,
            style: [
              {
                'color': '#000000'
              }
            ],
            bold: true,
            alignment: 'center',
          },
        ]);
        pdfContent.push({
          text: element.docSection,
          fontSize: 14,
          bold: true,
          margin: [0, 0, 0, 20],
          alignment: 'center',
        });

        docArea = element.docSection;
       
      }
      if (countDocs >= this.pdfDocPerPage) {
        pdfContent.push({
          // layout: 'lightHorizontalLines', // optional
          layout: 'customLayout',
          table: {
            // headers are automatically repeated if the table spans over multiple pages
            // you can declare how many rows should be treated as headers
            headerRows: 1,
            widths: [200, 200, 100, 100, 'auto'],
            heights: 20,
            body: tableBody,
          },
          pageBreak: 'after',
          pageOrientation: 'landscape'
        });

        countDocs = 0;
        tableBody = [];
        tableBody.push( [
          {
            text: this.translateService.instant("properties.ras.table.co1"),
            fontSize: 12,
            style: [
              {
                'color': '#000000'
              }
            ],
            bold: true,
            alignment: 'center',
          },
          {
            text: this.translateService.instant("properties.ras.table.co2"),
            fontSize: 12,
            style: [
              {
                'color': '#000000'
              }
            ],
            bold: true,
            alignment: 'center',
          },
          {
            text: this.translateService.instant("properties.ras.table.co3"),
            fontSize: 12,
            style: [
              {
                'color': '#000000'
              }
            ],
            bold: true,
            alignment: 'center',
          },
          {
            text: this.translateService.instant("properties.ras.table.co4"),
            fontSize: 12,
            style: [
              {
                'color': '#000000'
              }
            ],
            bold: true,
            alignment: 'center',
          },
          {
            text: this.translateService.instant("properties.ras.table.co5"),
            fontSize: 12,
            style: [
              {
                'color': '#000000'
              }
            ],
            bold: true,
            alignment: 'center',
          },
        ]);
        pdfContent.push({
          text: element.docSection,
          fontSize: 14,
          bold: true,
          margin: [0, 0, 0, 20],
          alignment: 'center',
        });
      }

      let docDate = "";
      if (
        element.dataDocumento && 
        moment(element.dataDocumento).isValid()
      ) {
        docDate = moment(element.dataDocumento).format("DD/MM/YYYY");
      }

      if (element.ragioneSocialeFornitore.length > this.supplierLong) {
        countDocs += 1;
      }
      countDocs += 1;
      if (element.qrcode !== '') {
        let qrImage = '';
        if (!element.reevaluated) {
          qrImage = document
            .getElementById(`qr-img-${element.index}`)
            .lastElementChild.lastElementChild.getAttribute("src");
        }
        
        tableBody.push([
          {
            text: element.structureObjectName,
            fontSize: 12,
            style: [
              {
                'color': '#28a745'
              }
            ],
            alignment: 'center',
          },
          {
            text: element.type,
            fontSize: 12,
            style: [
              {
                'color': '#28a745'
              }
            ],
            alignment: 'center',
          },
          {
            text: element.ragioneSocialeFornitore,
            fontSize: 12,
            style: [
              {
                'color': '#28a745'
              }
            ],
            alignment: 'center',
          },
          {
            text: docDate,
            fontSize: 12,
            style: [
              {
                'color': '#28a745'
              }
            ],
            alignment: 'center',
          },
          {
            image: element.reevaluated ? 'reevaluated' : qrImage,
            fit: [70, 70],
            alignment: 'center',
          },
        ]);
      } else {
        
        tableBody.push(
          [
            {
              text: element.structureObjectName,
              fontSize: 12,
              style: [
                {
                  'color': '#dc3545'
                }
              ],
              alignment: 'center',
            },
            {
              text: element.type,
              fontSize: 12,
              style: [
                {
                  'color': '#dc3545'
                }
              ],
              alignment: 'center',
            },
            {
              text: element.ragioneSocialeFornitore,
              fontSize: 12,
              style: [
                {
                  'color': '#28a745'
                }
              ],
              alignment: 'center',
            },
            {
              text: docDate,
              fontSize: 12,
              style: [
                {
                  'color': '#28a745'
                }
              ],
              alignment: 'center',
            },
            {
              image: element.urgent ? 'urgent' : 'necessary',
              fit: [63, 63],
              alignment: 'center',
            },
          ],
        );
      }
      // empty row
      tableBody.push(
        [
          {
            text: ' ',
            fontSize: 12,
          },
          {
            text: ' ',
            fontSize: 12,
          },
          {
            text: ' ',
            fontSize: 12,
          },
          {
            text: ' ',
            fontSize: 12,
          },
          {
            text: ' ',
            fontSize: 12,
          },
        ],
      );
    }
    pdfContent.push({
      // layout: 'lightHorizontalLines', // optional
      layout: 'customLayout',
      table: {
        // dontBreakRows: true,
        // headers are automatically repeated if the table spans over multiple pages
        // you can declare how many rows should be treated as headers
        headerRows: 1,
        widths: [200, 200, 100, 100, 'auto'],
        heights: 20,
        body: tableBody,
        // pageBreak: 'after'
      },
    });

    const pdfDefinition = {
      header: (currentPage: number, pageCount: number) => {
        if (currentPage === 1) {
          return [
            {
              image: 'logo',
              width: 111,
              height: 60,
              alignment: 'center',
              margin: [10, 10, 0, 20],
            },
          ]
        }
        return [
          {
            image: 'logo',
            width: 65,
            height: 30,
            alignment: 'center',
            margin: [10, 10, 0, 20],
          },
        ]
      },
      footer: (currentPage: number, pageCount: number) => {
        if (currentPage === 1) {
          return [
            {
              text: 'www.archedi.it                            info@archedi.it',
              color: '#e56c11',
              alignment: 'center',
              margin: [10, 10, 30, 10],
            }
          ];
        }
        return [
          {
            columns: [
              {
                text: `${this.propertyName} - ${this.propertyAddress}`,
                alignment: 'left',
                fontSize: 10,
                margin: [10, 10, 10, 5],
              },
              {
                text: `pag. ${currentPage.toString()} di ${pageCount.toString()}`,
                alignment: 'right',
                fontSize: 10,
                margin: [10, 10, 10, 5],
              }
            ]
          },
          // {
          //   columns: [
          //     {
          //       text: `${this.translateService.instant("properties.ras.creationDate.text")} ${createdAt}`,
          //       alignment: 'center',
          //       fontSize: 10,
          //       margin: [10, 10, 30, 10],
          //     },
          //   ]
          // },
        ];
      },
      content: pdfContent,
      images: {
        'logo': 'data:image/png;base64,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',
        'urgent': 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAFoAAABaCAIAAAC3ytZVAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAAJcEhZcwAADsMAAA7DAcdvqGQAAAVJSURBVHhe7db9TxN3HMBx/6mWzs0pyQoKKGRVJyNxijoJJttwYuIPqIsx00SjiaiwTpSw4EPlSYT5kBkdakLQADbbUL4tLa19gGuP68Ndr3e97nsPoB/c0AI1mnzeuR/KfXtfeq9+764rsPllMS3kACEHCDlAyAFCDhBygJADhBwg5AAhBwg5QMgBQg4QcoCQA4QcIOQAIQcIOUDIAUIOEHKAkAOEHCDkACEHCDlAyAFCDhBygJADhBwg5AAhByh3DuEf7pydsd8XJWNHNitLj64zZy6wgxHF2POxljsH2/dypZVYjydSxo5sNi007iSmtZPNrvfAofgfRRvtTO/zfPyvj5Bj6IynwErqbmWMHcvZsnMIQk8Lc7Y76XMnrrZFese0D50QH/ZFm1uj1x+LLIk1/co4RrUDtfgA39cRaW5juwZFTj/HhNDZwpzr5dmIcKcr0tQa7X6a5ul+WbzTxjTscZutxNYw3ehIBGXt/dmMfzjW/lvEfo17OCEvwWnZOTjuuxJSsNFTXk5MVlLTKUlM/Gi1i77WNteGrRP0u91xVT8yM34jWF6iD6lb2WHOlc5mpmdq1pGCLZNVm2aHity1XaIsJo9XTxSWqXtWlrvX/hD5i3KIKcdhz6ezM5jWeepviml9+pzLDwc97c0/MZf7uQG39OiUurZLD7JDvvSkkzuk0egc0nhkaymxbA06nOlImG9vmCgoctf9Lkkah2mtp96RdIfTzu5gSTGx7I6MqWth3sWijLX5PrOSiiMzIyEp6GTrthBzRaCH0QZzLj8cBVVTg6I2JCaPVRFTib89qN9WlLFLkxaDQxlt9lqsrtoeST+ztHP6y2JSeCQe1zgs1cyo/i3zsf3riXlT+E91TsiRFk5vJ6bSlw7GmH/4PJ3TfeC+9lfO5YfDsm162DgTbi/V+XpWB3BkehvmLqJX2yf72EDY4HimTyLED5QT88bQ/Tc5kur882agW02nOph7uXMkbgfohVp4NP4aB39qG+XwtXjf4BDUL7agcurxf3H0H6Ycru2n2StdM3ObYyAVm10db+fgY/X0VrLB93PHqxnodo+og7mXOwdd0jZ6JVdPjxhnmJX97K5SYraF/qB3/3kccqppt7pwmsaNxey008VsXCxjF1Ua2zlefWqoKdGwRF/rt9L/43jyOoecOv8tMRV7T47qFxw9WA6GZ1/nXO4c9GnXtld91JXtn2rt5bo6mPodLrPVVWnnBTo6jyOrkCu+VVby+a7Qhf5Yd3uoqkJdzPqtVA5w39so1kTNWbb/QcxxIWArn9h3K51ekOO5tr5KGyLdD4SwrPhvBr4oIpYt/hMdsbv3ZhoPeddsDtwIaQfm3CI46GkwSftB75pi40I1l3lqf4n79Y8+n4PeTcUbx7yri7Q3r/dU16oP2p3X9DElOhqt3+aie9TRYpftYPQpqyy4OrKSZ2bPRvX9xi0pIz+9HLRpz3V15wbvj+18dJHrY1EcegIjPhtJDjlTQX7hH6MKz2dm/MKTEcHDygMnKYer/q4xppbJhFz80Aj/Ipx5x7OQOfqv+b9fynPmGV564eSfOFOht3yYhVsCx7ulRAenvqr0HWnn7j2O91wK2uhdZnPwdtQY/sDKO0dW9MVP7POs0i8WK1n9TeDi6FJ+R+e1/HNoKYlwaniYHybp5IcqofWeOD6WkAOEHCDkACEHCDlAyAFCDhBygJADhBwg5AAhBwg5QMgBQg4QcoCQA4QcIOQAIQcIOUDIAUIOEHKAkAOEHCDkACEHCDlAyAFCDhBygJADhBwg5AAhB8jgwIxWrPgX2+axSBxEv+QAAAAASUVORK5CYII=',
        'necessary': 'data:image/png;base64,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',
        'reevaluated': 'data:image/png;base64,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',
      },
      pageMargins: [30, 60, 30, 60]
    };
    // Set pdf fonts
    pdfMake.vfs = pdfFonts.pdfMake.vfs;

    pdfMake.tableLayouts = {
      customLayout: {
        hLineWidth: function (i, node) {
          return 0.5;
        },
        vLineWidth: function (i) {
          return 0.5;
        },
        hLineColor: function (i) {
          return '#dbdbdb';
        },
        vLineColor: function (i) {
          return '#dbdbdb';
        },
        // paddingLeft: function (i) {
        //   return i === 0 ? 0 : 8;
        // },
        // paddingRight: function (i, node) {
        //   return (i === node.table.widths.length - 1) ? 0 : 8;
        // }
      }
    };

    pdfMake.createPdf(pdfDefinition).open();
  }

  /**
   * Handle dialog closing
   *
   * @since 1.0.0
   *
   */
  onClose(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());

    this.dialogRef.close();
  }
}
